import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Star, CheckCircle2, FlaskConical } from "lucide-react";
import Navbar from "../components/Navbar";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Page d'avis patient pour un laboratoire — extension hors cahier des
// charges (voir backend/review_links.py). Le lien est a usage unique,
// envoye par email quand le personnel marque le rendez-vous "termine"
// (routes/agenda.py) : pas de compte requis, comme la prise de RDV en ligne
// (ClinicProfile.js) et le portail patient (PatientPortalLab.js).
const LeaveReviewLab = () => {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | ready | invalid | used | sent | error
  const [info, setInfo] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/reviews/lab/${token}`)
      .then((res) => { setInfo(res.data); setStatus("ready"); })
      .catch((err) => {
        if (err?.response?.status === 410) setStatus("used");
        else setStatus("invalid");
      });
  }, [token]);

  const submit = async () => {
    if (!rating) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/reviews/lab/${token}`, { rating, comment: comment.trim() || null });
      setStatus("sent");
    } catch (err) {
      if (err?.response?.status === 410) setStatus("used");
      else setStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Laisser un avis | Tabbibak</title>
      </Helmet>
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="max-w-lg mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8 text-center">
            {status === "loading" && <p className="text-[#64748B]">Chargement...</p>}

            {status === "invalid" && (
              <>
                <p className="text-[#1A2B3C] font-semibold mb-2">Lien invalide</p>
                <p className="text-[#64748B]">Ce lien d'avis n'existe pas ou a expiré.</p>
              </>
            )}

            {status === "used" && (
              <>
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-[#1A2B3C] font-semibold mb-2">Avis déjà envoyé</p>
                <p className="text-[#64748B]">Vous avez déjà laissé un avis pour ce rendez-vous. Merci !</p>
              </>
            )}

            {status === "error" && (
              <>
                <p className="text-[#1A2B3C] font-semibold mb-2">Une erreur est survenue</p>
                <p className="text-[#64748B]">Merci de réessayer dans un instant.</p>
              </>
            )}

            {status === "sent" && (
              <>
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-[#1A2B3C] font-semibold mb-2">Merci pour votre avis !</p>
                <p className="text-[#64748B]">Il aidera d'autres patients à choisir en toute confiance.</p>
                <Link to="/" className="inline-block mt-4 text-[#0596DE] font-medium">Retour à l'accueil</Link>
              </>
            )}

            {status === "ready" && (
              <>
                <FlaskConical className="w-10 h-10 text-[#0596DE] mx-auto mb-3" />
                <h1 className="text-lg font-bold text-[#1A2B3C] mb-1">
                  Comment s'est passée votre visite chez {info?.clinic_name || "ce laboratoire"} ?
                </h1>
                <p className="text-[#64748B] text-sm mb-6">Bonjour {info?.patient_name}, votre avis ne prend qu'une minute.</p>

                <div className="flex items-center justify-center gap-1 mb-6">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHoverRating(n)}
                      onMouseLeave={() => setHoverRating(0)}
                      aria-label={`${n} étoile(s)`}
                    >
                      <Star
                        className={`w-9 h-9 ${
                          n <= (hoverRating || rating) ? "fill-amber-400 text-amber-400" : "text-[#E2E8F0]"
                        }`}
                      />
                    </button>
                  ))}
                </div>

                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Un commentaire à ajouter ? (facultatif)"
                  rows={4}
                  maxLength={1000}
                  className="w-full border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#1A2B3C] mb-4 resize-none"
                />

                <button
                  onClick={submit}
                  disabled={!rating || submitting}
                  className="w-full bg-[#0596DE] text-white py-3 rounded-lg font-semibold disabled:opacity-50 hover:bg-[#047CBA] transition-colors"
                >
                  {submitting ? "Envoi..." : "Envoyer mon avis"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LeaveReviewLab;
